import { useEffect } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { motion as Motion, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";
import { usePasswordStrength, PASSWORD_RULES } from "@/modules/auth/hooks/usePasswordStrength";

interface PasswordStrengthCheckerProps {
  password: string;
  onValidityChange?: (isValid: boolean) => void;
}

export function PasswordStrengthChecker({
  password,
  onValidityChange,
}: PasswordStrengthCheckerProps) {
  const { score, label, color, ruleStatus, isValid } = usePasswordStrength(password);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // Determine segment colors based on strength score
  const getSegmentColor = (index: number) => {
    if (score === 0) return "bg-gray-100";

    if (score <= 2) {
      return index === 0 ? "bg-red-500" : "bg-gray-100";
    }
    if (score <= 4) {
      return index <= 1 ? "bg-amber-500" : "bg-gray-100";
    }
    return "bg-emerald-500";
  };

  return (
    <div className="space-y-4 pt-1">
      <div className="space-y-2">
        <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-gray-400">
          <span>Force du mot de passe</span>
        </div>

        {/* Three Segmented Visual Indicator Bar */}
        <div className="grid grid-cols-3 gap-2 h-2">
          {[0, 1, 2].map((idx) => (
            <div
              key={idx}
              className={cn(
                "h-full rounded-full transition-all duration-300",
                getSegmentColor(idx),
              )}
            />
          ))}
        </div>

        {/* Text Label Indicator */}
        <AnimatePresence mode="wait">
          <Motion.p
            key={label}
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 2 }}
            transition={{ duration: 0.15 }}
            className={cn("text-xs font-bold transition-colors duration-300", color)}
          >
            {label}
          </Motion.p>
        </AnimatePresence>
      </div>

      {/* Rules Checklist */}
      <div className="space-y-2.5">
        <p className="text-xs font-bold text-gray-700">Le mot de passe doit contenir :</p>
        <ul className="space-y-2">
          {PASSWORD_RULES.map((rule) => {
            const isPassed = ruleStatus[rule.id] || false;
            return (
              <Motion.li
                key={rule.id}
                layout
                className="flex items-center gap-2.5 text-xs text-gray-600 font-medium"
              >
                <div className="shrink-0 transition-transform duration-300 active:scale-90">
                  {isPassed ? (
                    <CheckCircle2 size={16} className="text-emerald-500" />
                  ) : (
                    <XCircle size={16} className="text-red-500" />
                  )}
                </div>
                <span
                  className={cn(
                    "transition-all duration-300",
                    isPassed ? "text-gray-900 font-bold" : "text-gray-500",
                  )}
                >
                  {rule.label}
                </span>
              </Motion.li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
